import React, { useEffect } from 'react';
import { Box, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import { useDashboard } from '../Layouts/Dashboard';

/**
 * HeaderActions - A component to define actions that appear in the dashboard header
 * 
 * @param {boolean} onSave - If true, shows a Save button
 * @param {Function} onSaveClick - Function to call when Save button is clicked
 * @param {boolean} onPreview - If true, shows a Preview button
 * @param {Function} onPreviewClick - Function to call when Preview button is clicked
 * @param {boolean} onPrint - If true, shows a Print button
 * @param {Function} onPrintClick - Function to call when Print button is clicked
 * @param {boolean} onDownload - If true, shows a Download button
 * @param {Function} onDownloadClick - Function to call when Download button is clicked
 * @param {Array} customActions - Array of custom action buttons to render
 */
const HeaderActions = ({ 
  setHeaderActions: propSetHeaderActions, // For backward compatibility
  onSave,
  onSaveClick,
  onPreview,
  onPreviewClick,
  onPrint,
  onPrintClick,
  onDownload,
  onDownloadClick,
  customActions = [],
  children
}) => {
  // Get setHeaderActions from context
  const { setHeaderActions: contextSetHeaderActions } = useDashboard();
  
  // Use the prop version if provided (backward compatibility) or the context version
  const setHeaderActions = propSetHeaderActions || contextSetHeaderActions;

  // Generate the actions JSX
  const actionsContent = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {onSave && (
        <Button 
          variant="outlined" 
          startIcon={<SaveIcon />}
          onClick={onSaveClick}
          size="small"
        >
          Enregistrer
        </Button>
      )}
      
      {onPreview && (
        <Button 
          variant="outlined" 
          startIcon={<VisibilityIcon />}
          onClick={onPreviewClick}
          size="small"
        >
          Aperçu
        </Button>
      )}
      
      {onPrint && (
        <Button 
          variant="outlined" 
          startIcon={<PrintIcon />}
          onClick={onPrintClick}
          size="small"
        >
          Imprimer
        </Button>
      )}
      
      {onDownload && (
        <Button 
          variant="outlined" 
          startIcon={<FileDownloadIcon />}
          onClick={onDownloadClick}
          size="small"
        >
          Télécharger
        </Button>
      )}
      
      {customActions}
      {children}
    </Box>
  );
  
  // Use useEffect to set the header actions when the component mounts or updates
  useEffect(() => {
    if (setHeaderActions) {
      setHeaderActions(actionsContent);
    }
    
    // Clean up when component unmounts
    return () => {
      if (setHeaderActions) {
        setHeaderActions(null);
      }
    };
  }, [setHeaderActions, actionsContent]);
  
  // This component doesn't render anything itself
  return null;
};

export default HeaderActions; 